import _ from "underscore";
import moment from "moment";

import * as Analytics from "page/analytics";
import Constants from "const/constants";
import JSONVideoCategories from "json/socialvideocategories.min.json";

import SocialCredential from "./base";

var TwitterCredential = SocialCredential.extend({
    constants: {
        proMediaApiFeatures: {
            MEDIATOOL_MONETIZATION_USER: `mediatool_monetization_user`,
            MEDIATOOL_AMPLIFY_DIRECT: `mediatool_amplify_direct`,
            MEDIATOOL_UPLOAD_LONG: `mediatool_upload_long`
        }
    },

    initialize: function () {
        TwitterCredential.__super__.initialize.apply(this, arguments);
        _.bindAll(this, `onSponsorshipsFetchSuccess`);

        if (this.isProMediaEnabled()) {
            this.loadPersistedSettings();
        }
    },

    parse: function (response) {
        response = TwitterCredential.__super__.parse.apply(this, arguments);

        response.name = response.twitterUsername;
        response.profileImage = response.twitterProfileImageUrl;

        response.typeLabel = `X (Twitter)`;
        response.platformType = `X (Twitter) user`;

        return response;
    },

    toJSON: function () {
        var json = TwitterCredential.__super__.toJSON.apply(this, arguments);
        json.monetizationEnabled = this.isMonetizationEnabled();

        return json;
    },

    getDisplayName: function () {
        return `@${TwitterCredential.__super__.getDisplayName.apply(
            this,
            arguments
        )}`;
    },

    isSocialMedia: function () {
        return true;
    },

    isProMediaEnabled: function () {
        // Does the user have access to the Pro Media API?
        return this.has(`twitterProMediaApiFeatures`);
    },

    getProMediaApiFeatures: function () {
        if (this.isProMediaEnabled()) {
            return this.get(`twitterProMediaApiFeatures`);
        } else {
            return [];
        }
    },

    isMonetizationEnabled: function () {
        return (
            this.areVideoAdsEnabled() ||
            this.areSponsorshipsEnabled() ||
            this.areTwitterCardsEnabled()
        );
    },

    areVideoAdsEnabled: function () {
        if (!this.isProMediaEnabled()) {
            return false;
        }

        const features = this.get(`twitterProMediaApiFeatures`);
        return (
            features.indexOf(
                this.constants.proMediaApiFeatures.MEDIATOOL_MONETIZATION_USER
            ) >= 0
        );
    },

    areTwitterCardsEnabled: function () {
        return this.get(`twitterAdsEnabled`);
    },

    areSponsorshipsEnabled: function () {
        if (!this.isProMediaEnabled()) {
            return false;
        }

        const features = this.get(`twitterProMediaApiFeatures`);
        return (
            features.indexOf(
                this.constants.proMediaApiFeatures.MEDIATOOL_AMPLIFY_DIRECT
            ) >= 0
        );
    },

    fetchSponsorshipPrograms: function () {
        return $.get(
            `/ws/tools/twitter/monetization/sponsorships/${this.id}`
        ).done(this.onSponsorshipsFetchSuccess);
    },

    onSponsorshipsFetchSuccess: function (res) {
        this.set(`sponsorshipPrograms`, res.sponsorshipPrograms);
        // need to trigger that manually even if no change occurred
        this.trigger(`sponsorshipPrograms:change`);
    },

    onShareSuccessful: function (response) {
        return TwitterCredential.__super__.onShareSuccessful.call(
            this,
            `shareViaTwitterCount`,
            response
        );
    },

    getMonetizationShareInfo: function () {
        if (
            !this.get(`monetizationActive`) ||
            !this.has(`twitterMonetizationType`) ||
            this.get(`twitterMonetizationType`) ===
                Constants.TWITTER_MONETIZATION.NONE
        ) {
            return {};
        }

        let monetizationData;

        switch (this.get(`twitterMonetizationType`)) {
            case Constants.TWITTER_MONETIZATION.VIDEO_ADS:
                monetizationData = this.getVideoAdsSettings();
                break;
            case Constants.TWITTER_MONETIZATION.SPONSORSHIPS:
                monetizationData = this.getSponsorshipsSettings();
                break;
            case Constants.TWITTER_MONETIZATION.CARDS:
                monetizationData = this.getCardSettings();
                break;
        }

        Analytics.trackTwitterMonetization(
            this.get(`twitterMonetizationType`),
            this.extractVideoCategoryNames(
                monetizationData.twitterVideoCategoryIds
            )
        );

        return monetizationData;
    },

    extractVideoCategoryNames: function (categoryIds) {
        if (_.isUndefined(categoryIds)) {
            return;
        }
        const ids = categoryIds.split(`,`).map((str) => parseInt(str));
        const categories = JSONVideoCategories.twitter.categories;
        const filtered = categories.filter((cat) => {
            return ids.indexOf(cat.categoryId) >= 0;
        });
        return filtered.map((cat) => cat.categoryDescription);
    },

    getVideoAdsSettings: function () {
        return {
            twitterVideoCategoryIds: this.get(`twitterVideoCategoryIds`),
            twitterBlacklistVideoCategoryIds: this.get(
                `twitterBlacklistVideoCategoryIds`
            ),
            twitterBlacklistAdvertiserIds: this.get(
                `twitterBlacklistAdvertiserIds`
            ),
            twitterInStreamAdsCountryIds: this.get(
                `twitterInStreamAdsCountryIds`
            )
        };
    },

    getSponsorshipsSettings: function () {
        const expirationDate = this.get(
            `twitterSponsorshipProgramExpirationDate`
        );
        const twitterSponsorshipExpirationDate = moment(
            expirationDate,
            `h:mma, DD/MM/YYYY`
        ).isValid()
            ? moment(expirationDate, `h:mma, DD/MM/YYYY`).unix() * 1000
            : expirationDate * 1000;
        const twitterSponsorshipProgramExpirationDate =
            !_.isNaN(twitterSponsorshipExpirationDate) &&
            twitterSponsorshipExpirationDate > 0
                ? twitterSponsorshipExpirationDate
                : null;
        const sponsorshipSettings = {
            twitterSponsorshipProgramId: this.get(`twitterSponsorshipProgramId`)
        };

        if (!_.isNull(twitterSponsorshipProgramExpirationDate)) {
            sponsorshipSettings.twitterSponsorshipProgramExpirationDate =
                twitterSponsorshipExpirationDate;
        }

        return sponsorshipSettings;
    },

    getCardSettings: function () {
        return {
            twitterCardTitle: this.get(`twitterCardTitle`),
            twitterCardDestinationUrl: this.get(`twitterCardDestinationUrl`),
            twitterAdAccountId: this.get(`twitterAdAccountId`)
        };
    },

    share: function (shareId, data) {
        data = _.extend(data, this.getMonetizationShareInfo());
        return TwitterCredential.__super__.share.call(this, shareId, data);
    },

    getClipMaxDuration: function () {
        const features = this.getProMediaApiFeatures();
        const longVideoFeature =
            this.constants.proMediaApiFeatures.MEDIATOOL_UPLOAD_LONG;
        if (features.indexOf(longVideoFeature) >= 0) {
            return App.Constants.TWITTER_WHITELIST_LIMIT;
        }

        return App.Constants.TWITTER_NON_WHITELIST_LIMIT;
    }
});

export default TwitterCredential;
